import { Injectable } from '@angular/core';
import { ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error'; // Import onError
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
    FlyFreelyConstants,
    LoginManager
} from '@flyfreely-portal-ui/flyfreely';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Kind } from 'graphql';
import { Client, createClient } from 'graphql-ws';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

const KEEP_ALIVE_MS = 30 * 1000;
const MAX_RETRY_WAIT_MS = 30 * 1000;

@Injectable()
export class GraphqlSubscriptionStateService {
    private connectionChanged = new BehaviorSubject<
        'connecting' | 'reconnected' | 'disconnected'
    >('connecting');

    connectionChange$ = this.connectionChanged
        .asObservable()
        .pipe(distinctUntilChanged());

    private wsClient?: Client;

    constructor(
        apollo: Apollo,
        httpLink: HttpLink,
        constants: FlyFreelyConstants,
        private loginManager: LoginManager
    ) {
        loginManager.bearerToken$.subscribe(token => {
            if (token == null) {
                this.closeAll();
                return;
            }

            // Create Apollo link and WebSocket client with token
            const auth = setContext((operation, context) => {
                return {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                };
            });

            const http = httpLink.create({
                uri: `${constants.API_URL}/graphql`
            });

            const websocketUri =
                constants?.API_URL == null || constants.API_URL === ''
                    ? window.location.origin.replace('http', 'ws')
                    : constants.API_URL.replace('http', 'ws');

            // Reuse the client across token refreshes so active subscriptions stay on the
            // client that closeAll() terminates. Reconnects pick up the latest token anyway.
            if (this.wsClient == null) {
                this.wsClient = this.createWsClient(
                    `${websocketUri}/subscriptions`
                );
            }

            const ws = new GraphQLWsLink(this.wsClient);

            const errorLink = onError(({ networkError, graphQLErrors }) => {
                if (graphQLErrors) {
                    graphQLErrors.forEach(({ message, locations, path }) => {
                        console.error(
                            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
                        );
                    });
                }

                if (networkError) {
                    console.error(`[Network error]:`, networkError);
                }
            });

            const _split = split(
                ({ query }) => {
                    const data = getMainDefinition(query);
                    return (
                        data.kind === Kind.OPERATION_DEFINITION &&
                        data.operation === 'subscription'
                    );
                },
                ws,
                auth.concat(http)
            );

            const basic = setContext((operation, context) => ({
                headers: {
                    Accept: 'charset=utf-8'
                }
            }));

            const link = ApolloLink.from([basic, errorLink, _split]);

            // Replace the existing client
            apollo.removeClient();
            apollo.create({
                link: link,
                cache: new InMemoryCache(),
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: 'network-only'
                    },
                    query: {
                        fetchPolicy: 'network-only'
                    },
                    mutate: {}
                }
            });
        });
    }

    private createWsClient(url: string) {
        const wsClient = createClient({
            url,
            lazy: true,
            // Called on every (re)connect. After the browser resumes from suspension the
            // reconnect can happen before Keycloak's own refresh, so refresh here first.
            connectionParams: async () => {
                const token = await this.loginManager.getFreshBearerToken();
                return {
                    Authorization: `Bearer ${token}`
                };
            },
            // Ping periodically so idle connections aren't dropped by proxies.
            keepAlive: KEEP_ALIVE_MS,
            // By default only close events are retried, so a failed connection
            // (network drop, sleep/wake, server restart) errors every active subscription.
            // Fatal close codes, such as 4401 Unauthorized, are still never retried.
            shouldRetry: () => true,
            retryAttempts: Infinity,
            retryWait: async retries => {
                const delay = Math.min(1000 * 2 ** retries, MAX_RETRY_WAIT_MS);
                await new Promise(resolve =>
                    setTimeout(resolve, delay + Math.random() * 3000)
                );
            }
        });

        wsClient.on('connected', () => {
            this.connectionChanged.next('reconnected');
        });
        wsClient.on('closed', () => {
            this.connectionChanged.next('disconnected');
        });

        return wsClient;
    }

    /**
     * Forcefully close the websocket connection.
     */
    closeAll() {
        if (this.wsClient != null) {
            this.wsClient.terminate();
            this.wsClient.dispose();
            this.wsClient = undefined;
        }
    }
}
